import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, DollarSign, CalendarDays, Dumbbell } from 'lucide-react';
import api from '@/lib/api';
import { useGym } from '@/contexts/GymContext';

export default function OwnerGymPlans() {
    const { selectedGym } = useGym();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        name: '',
        description: '',
        price: '',
        duration_months: '1',
        total_classes: '0',
        is_active: true,
    });

    useEffect(() => {
        fetchPlans();
    }, [selectedGym]);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const params = selectedGym ? { gym_id: selectedGym.id } : {};
            const res = await api.get('/owner/gym-plans', { params });
            setPlans(res.data.data || []);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setError('');
        setForm({ name: '', description: '', price: '', duration_months: '1', total_classes: '0', is_active: true });
        setDialogOpen(true);
    };

    const openEdit = (plan) => {
        setEditing(plan);
        setError('');
        setForm({
            name: plan.name,
            description: plan.description || '',
            price: String(plan.price),
            duration_months: String(plan.duration_months),
            total_classes: String(plan.total_classes),
            is_active: plan.is_active,
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedGym) {
            setError('Seleccioná un gimnasio primero.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const payload = {
                ...form,
                gym_id: selectedGym.id,
                price: parseFloat(form.price),
                duration_months: parseInt(form.duration_months),
                total_classes: parseInt(form.total_classes),
            };

            if (editing) {
                await api.put(`/owner/gym-plans/${editing.id}`, payload);
            } else {
                await api.post('/owner/gym-plans', payload);
            }
            setDialogOpen(false);
            fetchPlans();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar. Verificá los datos e intentá de nuevo.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Eliminar este plan?')) return;
        try {
            await api.delete(`/owner/gym-plans/${id}`);
            fetchPlans();
        } catch {}
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(price);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold uppercase tracking-wide">Planes de Alumnos</h1>
                    <p className="text-white/60 text-sm mt-1">Crea planes de suscripción que los alumnos pueden comprar.</p>
                </div>
                <Button onClick={openCreate} className="uppercase tracking-widest text-xs">
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Plan
                </Button>
            </div>

            {!selectedGym && (
                <p className="text-white/60 text-sm">Seleccioná un gimnasio para ver y crear planes.</p>
            )}

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {plans.map(plan => (
                    <Card key={plan.id} className="border-hairline-dark bg-[#0a0a0a]">
                        <CardContent className="p-5 space-y-4">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                                    <p className="text-xs text-white/60 mt-0.5">{plan.description || 'Sin descripción'}</p>
                                </div>
                                {!plan.is_active && (
                                    <Badge variant="outline" className="border-white/30 text-white/60 text-[10px]">Inactivo</Badge>
                                )}
                            </div>

                            <div className="flex items-baseline gap-1">
                                <span className="text-2xl font-bold text-white">{formatPrice(plan.price)}</span>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-white/60">
                                <div className="flex items-center gap-1">
                                    <CalendarDays className="h-3.5 w-3.5" />
                                    <span>{plan.duration_months} {plan.duration_months === 1 ? 'mes' : 'meses'}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Dumbbell className="h-3.5 w-3.5" />
                                    <span>{plan.total_classes > 0 ? `${plan.total_classes} clases` : 'Ilimitado'}</span>
                                </div>
                            </div>

                            <div className="flex gap-2 pt-2">
                                <Button variant="outline" size="sm" className="flex-1 uppercase tracking-widest text-[10px]" onClick={() => openEdit(plan)}>
                                    <Pencil className="h-3 w-3 mr-1" />
                                    Editar
                                </Button>
                                <Button variant="outline" size="sm" className="flex-1 uppercase tracking-widest text-[10px] border-red-900/50 text-red-400 hover:bg-red-950" onClick={() => handleDelete(plan.id)}>
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Eliminar
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}

                {plans.length === 0 && selectedGym && (
                    <div className="col-span-full text-center py-12 text-white/40">
                        <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No hay planes creados para este gimnasio.</p>
                        <p className="text-xs mt-1">Creá tu primer plan para que los alumnos puedan pagar.</p>
                    </div>
                )}
            </div>

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-hairline-dark max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-bold uppercase tracking-wide">
                            {editing ? 'Editar Plan' : 'Nuevo Plan'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                        {error && (
                            <div className="text-sm p-3 rounded-md border border-red-800 bg-red-950/30 text-red-300">
                                {error}
                            </div>
                        )}
                        <div>
                            <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Nombre</label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Ej: Plan Mensual"
                                required
                                className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Descripción</label>
                            <Input
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="Ej: Acceso ilimitado por 1 mes"
                                className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Precio ($)</label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.price}
                                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                                    placeholder="15000"
                                    required
                                    className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Duración (meses)</label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="24"
                                    value={form.duration_months}
                                    onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
                                    required
                                    className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Clases incluidas (0 = ilimitado)</label>
                            <Input
                                type="number"
                                min="0"
                                value={form.total_classes}
                                onChange={(e) => setForm({ ...form, total_classes: e.target.value })}
                                className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                            />
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={form.is_active}
                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                className="h-4 w-4 rounded border-white/30 bg-[#0a0a0a] accent-white"
                            />
                            <label htmlFor="is_active" className="text-sm text-white/80">Activo</label>
                        </div>
                        <Button type="submit" disabled={saving} className="w-full uppercase tracking-widest text-xs">
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {editing ? 'Guardar Cambios' : 'Crear Plan'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
